import React, { useMemo, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart, Line, LabelList } from 'recharts';
import styles from './HistoricoComprasDetalhado.module.css';


/**
 * Calendário declarado de cada loja — hoje é só o PLANO B da média/dia útil.
 *
 *   e-commerce: vende todo dia (loja online, sem porta para fechar);
 *   centro:     fecha domingo;
 *   caxias:     abre só quarta, quinta, sexta e sábado.
 *
 * Números são o getDay() do JS: 0 domingo, 1 segunda ... 6 sábado.
 *
 * ⚠️ Quando o backend manda `diasOperacionais` (dias em que a loja de fato
 * vendeu no mês), é ELE que vale — o calendário fixo erra de três jeitos, todos
 * medidos em 15/09/2026: conta dia que ainda não sincronizou (Set/26 dividia por
 * 13 uma venda que só cobria 8 → média 38% abaixo), conta feriado (07/09 sem uma
 * venda em nenhuma loja) e estava errado para Caxias, que vendeu em 19 terças nos
 * últimos 180 dias apesar de cadastrada como "quarta a sábado".
 * Ver backend/services/diasOperacionaisLoja.js.
 */
const DIAS_FECHADOS_POR_LOJA = {
    ecommerce: [],
    centro: [0],
    caxias: [0, 1, 2],
};

/** Descrição em português, para a tela explicar a conta que está fazendo. */
const CALENDARIO_POR_LOJA = {
    ecommerce: 'todos os dias (loja online)',
    centro: 'segunda a sábado',
    caxias: 'quarta a sábado',
};

/**
 * Dias em que a loja ABRE dentro do mês.
 *
 * No mês CORRENTE conta só até hoje — dividir a venda parcial pelo mês inteiro
 * daria uma média artificialmente baixa justo no mês que o dono mais olha.
 */
function diasUteisDoMes(ano, mes, loja = 'centro') {
    const fechados = DIAS_FECHADOS_POR_LOJA[loja] || DIAS_FECHADOS_POR_LOJA.centro;
    const hoje = new Date();
    const ehMesCorrente = ano === hoje.getFullYear() && mes === hoje.getMonth() + 1;
    const ultimoDia = ehMesCorrente ? hoje.getDate() : new Date(ano, mes, 0).getDate();

    let uteis = 0;
    for (let dia = 1; dia <= ultimoDia; dia++) {
        if (!fechados.includes(new Date(ano, mes - 1, dia).getDay())) uteis++;
    }
    return uteis;
}

/**
 * Reposição do mês, venha de onde vier.
 *
 * E-COMMERCE: chega em `historicoEntradas` (lista de entradas com data).
 * LOJA FÍSICA: chega dentro do próprio mês de `historicoVendas`, em `qtdEntrada`
 * e `dataEntrada` — a API de loja física não manda `historicoEntradas`. Ler só a
 * primeira fonte fazia a coluna Reposição mostrar "—" em todo mês da loja física,
 * mesmo com entrada registrada (ex.: 2.122 un em 11/06/2026).
 */
function reposicaoDoMes(mesHistorico, entradaDoMapa) {
    if (entradaDoMapa && entradaDoMapa.quantidade > 0) {
        return { quantidade: entradaDoMapa.quantidade, datas: entradaDoMapa.datas || [] };
    }
    const qtd = Number(mesHistorico?.qtdEntrada || 0);
    if (qtd > 0) {
        return { quantidade: qtd, datas: mesHistorico.dataEntrada ? [mesHistorico.dataEntrada] : [] };
    }
    return { quantidade: 0, datas: [] };
}

const HistoricoComprasDetalhado = ({ historicoVendas = [], estoque = 0, mediaMensal = 0, tipo = 'ecommerce', loja = '', variacoes = [], historicoEntradas = [], saldosMensais = {} }) => {
    const [expandedMonths, setExpandedMonths] = useState({});

    const isLojaFisica = tipo === 'centro' || tipo === 'caxias';
    // `loja` vem da página (centro/caxias/ecommerce). O `tipo` da rota é
    // "loja-fisica", que não diferencia as duas lojas — por isso a prop existe.
    const lojaCalendario = DIAS_FECHADOS_POR_LOJA[loja] ? loja
        : (tipo === 'ecommerce' ? 'ecommerce' : 'centro');

    const toggleMonth = (key) => {
        setExpandedMonths(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    // Ordenar do mais antigo para o mais recente (para gráfico)
    const historicoOrdenado = useMemo(() => {
        if (!historicoVendas || historicoVendas.length === 0) return [];

        return [...historicoVendas].sort((a, b) => {
            if (a.ano !== b.ano) return a.ano - b.ano;
            const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
            const mesA = typeof a.mes === 'number' ? a.mes : meses.indexOf(a.mes) + 1;
            const mesB = typeof b.mes === 'number' ? b.mes : meses.indexOf(b.mes) + 1;
            return mesA - mesB;
        });
    }, [historicoVendas]);

    // Agrupa historicoEntradas por ano-mês usando extração de string (sem new Date cru para evitar shift de fuso).
    // Resultado: Map<'YYYY-M', { quantidade: number, datas: string[], origens: string[] }>
    // DEVE vir antes de qualquer memo que dependa de entradasPorMes.
    const entradasPorMes = useMemo(() => {
        const map = new Map();
        if (!historicoEntradas || historicoEntradas.length === 0) return map;
        historicoEntradas.forEach(e => {
            const m = String(e.data || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (!m) return;
            const ano = Number(m[1]);
            const mes = Number(m[2]);
            const key = `${ano}-${mes}`;
            if (!map.has(key)) map.set(key, { ano, mes, quantidade: 0, datas: [], origens: [] });
            const slot = map.get(key);
            slot.quantidade += (e.quantidade || 0);
            slot.datas.push(e.data);
            if (e.origem) slot.origens.push(e.origem);
        });
        return map;
    }, [historicoEntradas]);

    // Calcular estatísticas — totalEntrada e numEntradas derivados de historicoEntradas (fonte real)
    const estatisticas = useMemo(() => {
        const totalVendido = historicoOrdenado.reduce((sum, h) => sum + (h.quantidade || 0), 0);
        const mesesComVenda = historicoOrdenado.filter(h => h.quantidade > 0).length;
        const totalPedidos = historicoOrdenado.reduce((sum, h) => sum + (h.numeroPedidos || 0), 0);

        // Reposição vem de DUAS fontes conforme a origem do produto: `historicoEntradas`
        // (e-commerce) e `qtdEntrada` dentro de cada mês (loja física). Somar só a
        // primeira zerava o total da loja física — que era o caso desta tela.
        const entradasDoHistorico = historicoOrdenado.filter(h => Number(h.qtdEntrada || 0) > 0);
        const totalEntrada = historicoEntradas.length
            ? historicoEntradas.reduce((sum, e) => sum + (e.quantidade || 0), 0)
            : entradasDoHistorico.reduce((sum, h) => sum + Number(h.qtdEntrada || 0), 0);
        const numEntradas = historicoEntradas.length || entradasDoHistorico.length;

        // Calcular média mensal real
        const mediaReal = mesesComVenda > 0 ? totalVendido / mesesComVenda : 0;

        // Dias de cobertura
        const diasCobertura = mediaReal > 0 ? Math.round((estoque / mediaReal) * 30) : null;

        return {
            totalVendido,
            totalEntrada,
            mesesComVenda,
            mesesComEntrada: numEntradas,   // "em N entradas" no card
            totalPedidos,
            mediaReal,
            diasCobertura
        };
    }, [historicoOrdenado, historicoEntradas, estoque]);

    // Preparar dados para o gráfico — entradas lidas de entradasPorMes (fonte real)
    const dadosGrafico = useMemo(() => {
        const MESES_NOME = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

        // Pontos vindos de historicoVendas
        const pontosVendas = historicoOrdenado.map(h => {
            const mesNum = typeof h.mes === 'number' ? h.mes : MESES_NOME.indexOf(h.mes) + 1;
            const mesNome = typeof h.mes === 'number' ? MESES_NOME[h.mes - 1] : h.mes;
            const entradaMes = entradasPorMes.get(`${h.ano}-${mesNum}`);
            // Mesma leitura da tabela: e-commerce traz em historicoEntradas, loja
            // física traz em qtdEntrada dentro do mês. Só a primeira fonte deixava
            // o gráfico da loja física sem nenhuma barra verde.
            const { quantidade: qtdEntradaMes } = reposicaoDoMes(h, entradaMes);
            return {
                _ano: h.ano,
                _mes: mesNum,
                periodo: `${mesNome}/${String(h.ano).slice(-2)}`,
                vendas: h.quantidade || 0,
                entradas: qtdEntradaMes,
                mediaHistorica: Math.round(mediaMensal)
            };
        });

        // Acrescentar meses que só existem em entradasPorMes (sem venda — ex: T291)
        const anosMesesVenda = new Set(pontosVendas.map(p => `${p._ano}-${p._mes}`));
        const pontosExtra = [];
        entradasPorMes.forEach((slot) => {
            if (!anosMesesVenda.has(`${slot.ano}-${slot.mes}`)) {
                pontosExtra.push({
                    _ano: slot.ano,
                    _mes: slot.mes,
                    periodo: `${MESES_NOME[slot.mes - 1]}/${String(slot.ano).slice(-2)}`,
                    vendas: 0,
                    entradas: slot.quantidade,
                    mediaHistorica: Math.round(mediaMensal)
                });
            }
        });

        // Ordenar tudo cronologicamente
        return [...pontosVendas, ...pontosExtra].sort((a, b) =>
            a._ano !== b._ano ? a._ano - b._ano : a._mes - b._mes
        );
    }, [historicoOrdenado, entradasPorMes, mediaMensal]);

    // Linhas da tabela mensal: une historicoVendas com meses que só existem em historicoEntradas
    // (ex: T291 tem entrada em Jun/26 mas zero venda — precisa aparecer na tabela)
    const linhasTabela = useMemo(() => {
        const MESES_NOME = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
        const base = historicoOrdenado.map(h => {
            const mesNum = typeof h.mes === 'number' ? h.mes : MESES_NOME.indexOf(h.mes) + 1;
            return { ...h, _mesNum: mesNum };
        });

        const anosMesesVenda = new Set(base.map(h => `${h.ano}-${h._mesNum}`));
        const extras = [];
        entradasPorMes.forEach((slot) => {
            if (!anosMesesVenda.has(`${slot.ano}-${slot.mes}`)) {
                extras.push({
                    ano: slot.ano,
                    mes: slot.mes,
                    _mesNum: slot.mes,
                    quantidade: 0,
                    numeroPedidos: 0,
                    pedidos: [],
                });
            }
        });

        return [...base, ...extras]
            .sort((a, b) => a.ano !== b.ano ? a.ano - b.ano : a._mesNum - b._mesNum)
            .reverse(); // mais recente primeiro (igual ao original)
    }, [historicoOrdenado, entradasPorMes]);

    // Timeline de reposições (para e-commerce e loja física)
    const timelineReposicoes = useMemo(() => {
        return historicoOrdenado
            .filter(h => h.qtdEntrada > 0)
            .map(h => {
                const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
                const mesNome = typeof h.mes === 'number' ? meses[h.mes - 1] : h.mes;
                return {
                    periodo: `${mesNome}/${h.ano}`,
                    quantidade: h.qtdEntrada,
                    data: h.dataEntrada ? new Date(h.dataEntrada).toLocaleDateString('pt-BR') : null
                };
            });
    }, [historicoOrdenado]);

    const formatarMoeda = (valor) => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(valor || 0);
    };

    // Formata a data da entrada ("YYYY-MM-DD") como "DD/MM" sem aplicar fuso —
    // new Date('YYYY-MM-DD') seria interpretada como UTC e mostraria o dia anterior no BRT.
    const formatarDiaEntrada = (dataStr) => {
        if (!dataStr) return null;
        const m = String(dataStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (m) return `${m[3]}/${m[2]}`;
        const d = new Date(dataStr);
        return isNaN(d.getTime()) ? null : d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    };

    // Data da entrada no mesmo formato das vendas (seg., 08/06/2026), montando
    // a data a partir das partes pra não sofrer shift de fuso.
    // precisao 'mes' = registro do relatório mensal do Bling (o dia é fictício, sempre 01)
    // → exibe só "Jun/2026" pra não inventar dia (caso T351: entrada real 17/06 aparecia como 01/06).
    const MESES_ABREV_ENTRADA = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    const formatarDataEntradaCompleta = (dataStr, precisao) => {
        if (!dataStr) return null;
        const m = String(dataStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (m && precisao === 'mes') return `${MESES_ABREV_ENTRADA[Number(m[2]) - 1]}/${m[1]}`;
        const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(dataStr);
        return isNaN(d.getTime()) ? null : d.toLocaleDateString('pt-BR', {
            weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric'
        });
    };

    // Só mostra empty-state se NÃO houver vendas E NÃO houver entradas
    if ((!historicoVendas || historicoVendas.length === 0) && entradasPorMes.size === 0) {
        return (
            <div className={styles.container}>
                <h2 className={styles.title}>
                    📦 Histórico de Compras e Reposições
                </h2>
                <div className={styles.emptyState}>
                    <span className={styles.emptyIcon}>📊</span>
                    <p>Nenhum histórico de vendas disponível para este produto.</p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <h2 className={styles.title}>
                📦 Histórico de Compras e Reposições
            </h2>

            {/* Detalhamento Mensal — PRIMEIRO (movido pra cima por design da página de detalhes) */}
            <div className={styles.tableSection}>
                <h3 className={styles.sectionSubtitle}>
                    📋 Detalhamento Mensal {historicoOrdenado.some(h => h.pedidos?.length > 0) && <span className={styles.subtitleHint}>· clique nos meses pra ver os dias</span>}
                </h3>
                <div className={styles.tableWrapper}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th></th>
                                <th>Período</th>
                                <th title={`Vendas do mês divididas pelos dias em que a loja de fato vendeu — feriado, dia fechado e dia que ainda não chegou do caixa ficam fora da conta. Calendário da loja: ${CALENDARIO_POR_LOJA[lojaCalendario]}.`}>
                                    {lojaCalendario === 'ecommerce' ? 'Média/dia' : 'Média/dia útil'}
                                </th>
                                <th>Vendas</th>
                                <th>Dias</th>
                                <th>Reposição</th>
                            </tr>
                        </thead>
                        <tbody>
                            {linhasTabela.map((h, idx) => {
                                const MESES_NOME = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
                                const mesNome = typeof h.mes === 'number' ? MESES_NOME[h.mes - 1] : h.mes;
                                const mesNum = h._mesNum || (typeof h.mes === 'number' ? h.mes : MESES_NOME.indexOf(h.mes) + 1);

                                // Reposição: entradasPorMes (e-commerce) OU qtdEntrada do mês (loja física)
                                const entradaMes = entradasPorMes.get(`${h.ano}-${mesNum}`);
                                const { quantidade: qtdReposta, datas: datasReposta } = reposicaoDoMes(h, entradaMes);

                                // Divisor da média: dias em que a loja realmente vendeu no mês
                                // (vem do backend). Só cai no calendário fixo quando o mês está
                                // fora da janela de vendas guardada — aí é a melhor aproximação
                                // disponível, e o tooltip diz qual das duas foi usada.
                                const diasReais = Number(h.diasOperacionais || 0);
                                const uteis = diasReais > 0 ? diasReais : diasUteisDoMes(h.ano, mesNum, lojaCalendario);
                                const mediaDiaUtil = uteis > 0 ? (h.quantidade || 0) / uteis : null;
                                const origemDivisor = diasReais > 0
                                    ? 'dias em que a loja vendeu'
                                    : `estimado pelo calendário: ${CALENDARIO_POR_LOJA[lojaCalendario]}`;

                                const monthKey = `${h.ano}-${h.mes}`;
                                const isExpanded = expandedMonths[monthKey];
                                const hasPedidos = h.pedidos && h.pedidos.length > 0;

                                return (
                                    <React.Fragment key={idx}>
                                        <tr
                                            className={`${!isLojaFisica && qtdReposta > 0 ? styles.rowComEntrada : ''} ${hasPedidos ? styles.expandableRow : ''}`}
                                            onClick={() => hasPedidos && toggleMonth(monthKey)}
                                            style={{ cursor: hasPedidos ? 'pointer' : 'default' }}
                                        >
                                            <td className={styles.expandCell}>
                                                {hasPedidos && (
                                                    <span className={`${styles.expandIcon} ${isExpanded ? styles.expanded : ''}`}>
                                                        ▶
                                                    </span>
                                                )}
                                            </td>
                                            <td><strong>{mesNome}/{h.ano}</strong></td>
                                            <td title={`${h.quantidade || 0} un ÷ ${uteis} dia(s) — ${origemDivisor}`}>
                                                {mediaDiaUtil == null ? (
                                                    <span className={styles.semPedido}>—</span>
                                                ) : (
                                                    <span className={styles.vendaValue}>
                                                        {mediaDiaUtil.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} un/dia
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                <span className={styles.vendaValue}>
                                                    {h.quantidade || 0} un
                                                </span>
                                            </td>
                                            <td>
                                                {hasPedidos ? (
                                                    <span className={styles.pedidosBadge}>
                                                        {h.pedidos.length} dias
                                                    </span>
                                                ) : (
                                                    <span className={styles.semPedido}>—</span>
                                                )}
                                            </td>
                                            <td>
                                                {qtdReposta > 0 ? (
                                                    <div className={styles.reposicaoCell}>
                                                        <span className={styles.entradaBadge}>
                                                            +{qtdReposta}
                                                        </span>
                                                        {/* Mostrar data(s) da(s) entrada(s) do mês */}
                                                        {datasReposta.map((d, di) => (
                                                            <span key={di} className={styles.dataEntrada}>
                                                                {formatarDiaEntrada(d)}
                                                            </span>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <span className={styles.semEntrada}>—</span>
                                                )}
                                            </td>
                                        </tr>

                                        {isExpanded && hasPedidos && (
                                            <tr className={styles.expandedRow}>
                                                <td colSpan="6">
                                                    <div className={styles.pedidosDetail}>
                                                        <div className={styles.pedidosHeader}>
                                                            Vendas por dia — {mesNome}/{h.ano}
                                                        </div>
                                                        <div className={styles.pedidosList}>
                                                            {/* Entradas reais do mês no painel expandido */}
                                                            {entradaMes && entradaMes.datas.map((dataE, ei) => {
                                                                // Encontra o item original para pegar origem
                                                                const itemOriginal = (historicoEntradas || []).find(e => e.data === dataE);
                                                                return (
                                                                    <div key={`e-${ei}`} className={`${styles.pedidoItem} ${styles.reposicaoLinha}`}>
                                                                        <div className={styles.pedidoData}>
                                                                            {formatarDataEntradaCompleta(dataE, itemOriginal?.precisao)}
                                                                        </div>
                                                                        <div className={styles.pedidoInfo}>
                                                                            <span className={styles.reposicaoTag}>
                                                                                {itemOriginal?.origem || 'Reposição de estoque'}
                                                                            </span>
                                                                            <span className={styles.entradaBadge}>
                                                                                +{itemOriginal?.quantidade || ''} un
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                            {h.pedidos.map((venda, pIdx) => (
                                                                <div key={pIdx} className={styles.pedidoItem}>
                                                                    <div className={styles.pedidoData}>
                                                                        {new Date(venda.data).toLocaleDateString('pt-BR', {
                                                                            weekday: 'short',
                                                                            day: '2-digit',
                                                                            month: '2-digit',
                                                                            year: 'numeric'
                                                                        })}
                                                                    </div>
                                                                    <div className={styles.pedidoInfo}>
                                                                        {isLojaFisica ? (
                                                                            <span className={styles.pedidoLoja}>
                                                                                {venda.loja}
                                                                            </span>
                                                                        ) : (
                                                                            <span className={styles.pedidoId}>
                                                                                {venda.totalPedidos > 1
                                                                                    ? `${venda.totalPedidos} pedidos`
                                                                                    : '1 pedido'}
                                                                            </span>
                                                                        )}
                                                                        <span className={styles.pedidoQtd}>
                                                                            {venda.quantidade} un
                                                                        </span>
                                                                        <span className={styles.pedidoValor}>
                                                                            {formatarMoeda(venda.valorTotal)}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr className={styles.totalRow}>
                                <td></td>
                                <td><strong>TOTAL</strong></td>
                                {/* Média não se soma: a coluna fica vazia de propósito. Sem esta
                                    célula o rodapé tinha 5 colunas contra 6 do cabeçalho e todos
                                    os totais apareciam uma coluna à esquerda do rótulo certo. */}
                                <td></td>
                                <td><strong>{estatisticas?.totalVendido || 0} un</strong></td>
                                <td><strong>{estatisticas?.totalPedidos || 0} dias</strong></td>
                                <td><strong className={styles.entradaTotal}>+{estatisticas?.totalEntrada || 0}</strong></td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>

            {/* Cards de Resumo */}
            <div className={styles.cardsGrid}>
                <div className={styles.statCard}>
                    <div className={styles.statIcon}>🛒</div>
                    <div className={styles.statContent}>
                        <span className={styles.statLabel}>Total Vendido</span>
                        <span className={styles.statValue}>{estatisticas?.totalVendido || 0}</span>
                        <span className={styles.statSubtext}>
                            em {estatisticas?.totalPedidos || 0} dias com vendas
                        </span>
                    </div>
                </div>

                <div className={styles.statCard}>
                    <div className={styles.statIcon}>📦</div>
                    <div className={styles.statContent}>
                        <span className={styles.statLabel}>Total Reposto</span>
                        <span className={styles.statValue} style={{ color: '#27ae60' }}>{estatisticas?.totalEntrada || 0}</span>
                        <span className={styles.statSubtext}>em {estatisticas?.mesesComEntrada || 0} entradas</span>
                    </div>
                </div>

                <div className={styles.statCard}>
                    <div className={styles.statIcon}>📈</div>
                    <div className={styles.statContent}>
                        <span className={styles.statLabel}>Média Real/Mês</span>
                        <span className={styles.statValue}>{(estatisticas?.mediaReal || 0).toFixed(1)}</span>
                        <span className={styles.statSubtext}>unidades vendidas</span>
                    </div>
                </div>

                <div className={styles.statCard}>
                    <div className={styles.statIcon}>⏳</div>
                    <div className={styles.statContent}>
                        <span className={styles.statLabel}>Cobertura Estoque</span>
                        <span className={styles.statValue} style={{
                            color: (estatisticas?.diasCobertura || 0) < 30 ? '#e74c3c' :
                                (estatisticas?.diasCobertura || 0) < 60 ? '#f39c12' : '#27ae60'
                        }}>
                            {estatisticas?.diasCobertura ? `${estatisticas.diasCobertura} dias` : 'N/A'}
                        </span>
                        <span className={styles.statSubtext}>estoque atual: {estoque}</span>
                    </div>
                </div>

                {isLojaFisica && (
                    <div className={styles.statCard}>
                        <div className={styles.statIcon}>📅</div>
                        <div className={styles.statContent}>
                            <span className={styles.statLabel}>Meses Ativos</span>
                            <span className={styles.statValue}>{estatisticas?.mesesComVenda || 0}</span>
                            <span className={styles.statSubtext}>de {historicoOrdenado.length} meses</span>
                        </div>
                    </div>
                )}
            </div>

            {/* Seção de Variações (Tamanhos) - apenas para produtos com variações */}
            {variacoes && variacoes.length > 0 && (
                <div className={styles.variacoesSection}>
                    <h3 className={styles.sectionSubtitle}>
                        📏 Vendas por Tamanho/Variação
                    </h3>
                    <div className={styles.variacoesGrid}>
                        {variacoes.map((variacao, idx) => (
                            <div key={idx} className={styles.variacaoCard}>
                                <div className={styles.variacaoTamanho}>
                                    {variacao.tamanho ? `Tam. ${variacao.tamanho}` : variacao.codigo}
                                </div>
                                <div className={styles.variacaoStats}>
                                    <span className={styles.variacaoVendas}>
                                        🛒 {variacao.vendas} vendas
                                    </span>
                                    <span className={styles.variacaoEstoque} style={{
                                        color: variacao.estoque === 0 ? '#e74c3c' :
                                            variacao.estoque < 5 ? '#f39c12' : '#27ae60'
                                    }}>
                                        📦 {variacao.estoque} em estoque
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className={styles.variacoesSummary}>
                        <span>Total: {variacoes.reduce((sum, v) => sum + v.vendas, 0)} vendas em {variacoes.length} variações</span>
                    </div>
                </div>
            )}

            {/* Gráfico Combinado */}
            <div className={styles.chartSection}>
                <h3 className={styles.sectionSubtitle}>
                    📊 Vendas x Reposições por Mês
                </h3>
                <div className={styles.chartContainer}>
                    <ResponsiveContainer width="100%" height={350}>
                        <ComposedChart data={dadosGrafico} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                            <XAxis
                                dataKey="periodo"
                                tick={{ fontSize: 12, fill: '#666' }}
                                tickLine={{ stroke: '#ccc' }}
                            />
                            {/* Dois eixos de propósito: reposição e venda têm ordens de grandeza
                                diferentes (1 reposição contra 260 vendas). Num eixo só, a barra
                                verde ficava menor que um pixel e o gráfico parecia não ter
                                reposição nenhuma. */}
                            <YAxis
                                yAxisId="vendas"
                                tick={{ fontSize: 12, fill: '#666' }}
                                tickLine={{ stroke: '#ccc' }}
                            />
                            <YAxis
                                yAxisId="reposicoes"
                                orientation="right"
                                allowDecimals={false}
                                tick={{ fontSize: 12, fill: '#27ae60' }}
                                tickLine={{ stroke: '#27ae60' }}
                            />
                            <Tooltip
                                contentStyle={{
                                    background: 'rgba(255,255,255,0.95)',
                                    border: '1px solid #ddd',
                                    borderRadius: '8px',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                }}
                                formatter={(value, name) => {
                                    const labels = {
                                        vendas: 'Vendas (un)',
                                        entradas: 'Reposições (un)',
                                        mediaHistorica: 'Média Mensal (un)'
                                    };
                                    return [value, labels[name] || name];
                                }}
                            />
                            <Legend
                                formatter={(value) => {
                                    const labels = {
                                        vendas: 'Vendas (eixo esquerdo)',
                                        entradas: 'Reposições (eixo direito)',
                                        mediaHistorica: 'Média Mensal'
                                    };
                                    return labels[value] || value;
                                }}
                            />
                            <Bar
                                yAxisId="vendas"
                                dataKey="vendas"
                                fill="#004B87"
                                radius={[4, 4, 0, 0]}
                                name="vendas"
                            />
                            <Bar
                                yAxisId="reposicoes"
                                dataKey="entradas"
                                fill="#27ae60"
                                radius={[4, 4, 0, 0]}
                                name="entradas"
                                minPointSize={3}
                            >
                                {/* Número em cima da barra: com reposição pequena, o rótulo é
                                    o que realmente informa a quantidade. */}
                                <LabelList
                                    dataKey="entradas"
                                    position="top"
                                    fontSize={11}
                                    fill="#27ae60"
                                    formatter={(v) => (v > 0 ? `+${v}` : '')}
                                />
                            </Bar>
                            <Line
                                yAxisId="vendas"
                                type="monotone"
                                dataKey="mediaHistorica"
                                stroke="#e74c3c"
                                strokeWidth={2}
                                strokeDasharray="5 5"
                                dot={false}
                                name="mediaHistorica"
                            />
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            </div>

            {/* Timeline de Reposições — evento a evento se historicoEntradas disponível, fallback mensal */}
            {historicoEntradas.length > 0 ? (
                <div className={styles.timelineSection}>
                    <h3 className={styles.sectionSubtitle}>📅 Histórico de Reposições</h3>
                    <div className={styles.timeline}>
                        {historicoEntradas.map((entrada, index) => (
                            <div key={index} className={styles.timelineItem}>
                                <div className={styles.timelineDot}></div>
                                <div className={styles.timelineContent}>
                                    <span className={styles.timelinePeriodo}>
                                        {formatarDataEntradaCompleta(entrada.data, entrada.precisao)}
                                    </span>
                                    <span className={styles.timelineQtd}>+{entrada.quantidade} un</span>
                                    {entrada.origem && (
                                        <span className={styles.timelineData}>{entrada.origem}</span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : timelineReposicoes.length > 0 ? (
                <div className={styles.timelineSection}>
                    <h3 className={styles.sectionSubtitle}>📅 Histórico de Reposições</h3>
                    <div className={styles.timeline}>
                        {timelineReposicoes.map((rep, index) => (
                            <div key={index} className={styles.timelineItem}>
                                <div className={styles.timelineDot}></div>
                                <div className={styles.timelineContent}>
                                    <span className={styles.timelinePeriodo}>{rep.periodo}</span>
                                    <span className={styles.timelineQtd}>+{rep.quantidade} un</span>
                                    {rep.data && (
                                        <span className={styles.timelineData}>{rep.data}</span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : null}

        </div>
    );
};

export default HistoricoComprasDetalhado;
